import React, { useEffect, useState } from 'react';
import { Modal, Typography, Switch, Button, Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { useSMSSettings } from '../../../hooks/organizations/useSMSSettings';
import { useToast } from '@crisellevaldez/speculo-ui';
import { Info } from 'lucide-react';

interface SMSModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

export const SMSModal: React.FC<SMSModalProps> = ({ isOpen, onClose, selectedOrg, onSuccess }) => {
  const [smsNotifications, setSmsNotifications] = useState(selectedOrg?.sms?.notifications ?? true);
  const [smsAllOutcomes, setSmsAllOutcomes] = useState(selectedOrg?.smsAllOutcomes ?? false);
  const { updateSettings, isPending } = useSMSSettings();
  const { showToast } = useToast();

  useEffect(() => {
    setSmsNotifications(selectedOrg?.sms?.notifications ?? true);
    setSmsAllOutcomes(selectedOrg?.smsAllOutcomes ?? false);
  }, [selectedOrg?.id, selectedOrg?.sms?.notifications, selectedOrg?.smsAllOutcomes]);

  const handleSave = async () => {
    if (!selectedOrg?.id) return;

    try {
      await updateSettings({
        orgId: selectedOrg.id,
        data: {
          sms: {
            notifications: smsNotifications,
          },
          smsAllOutcomes: smsAllOutcomes,
        },
      });

      showToast({
        message: 'SMS settings updated successfully',
        variant: 'success',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating SMS settings:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update SMS settings',
        variant: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">SMS Settings</Typography>
      </Modal.Header>
      <Modal.Body className="pb-6">
        <div className="space-y-6">
          <Alert variant="info">
            <Info className="h-4 w-4" />
            <AlertDescription>
              Configure agent notification text messages for this organization.
            </AlertDescription>
          </Alert>

          <div className="bg-gradient-to-br from-gray-50 to-white dark:from-white/5 dark:to-transparent border border-gray-200 dark:border-white/15 rounded-xl p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Typography variant="h5" className="font-bold text-gray-900 dark:text-gray-100">
                    Text Message Notifications
                  </Typography>
                </div>
                <Typography variant="small" className="text-gray-600 dark:text-gray-400 font-normal leading-relaxed">
                  Enable agent notification texts for handoffs and opportunity calls.
                </Typography>
              </div>
              <Switch
                checked={smsNotifications}
                onChange={(e) => setSmsNotifications(e.target.checked)}
                disabled={isPending}
                size="sm"
              />
            </div>
          </div>

          {/* SMS All Outcomes Switch - Available independently */}
          <div className="bg-gradient-to-br from-gray-50 to-white dark:from-white/5 dark:to-transparent border border-gray-200 dark:border-white/15 rounded-xl p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Typography variant="h5" className="font-bold text-gray-900 dark:text-gray-100">
                    All Call Outcomes
                  </Typography>
                </div>
                <Typography variant="small" className="text-gray-600 dark:text-gray-400 font-normal leading-relaxed">
                  When enabled, agent notification texts apply to all call outcomes. When disabled,
                  they only apply to nurture and follow-up calls.
                </Typography>
              </div>
              <Switch
                checked={smsAllOutcomes}
                onChange={(e) => setSmsAllOutcomes(e.target.checked)}
                disabled={isPending || !smsNotifications}
                size="sm"
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={isPending} disabled={isPending}>
            Save Changes
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
